import { useEffect, useContext, createContext, FC, useState } from "react";
import { useSelector } from "react-redux";
import * as _ from "lodash";

import {
  DataDescriptorEntity,
  EnterpriseAppState,
  Enterprise,
  ChartDataSourceType,
  UnitSystemConversionMap,
  ChartEntity
} from "@ctra/api";

import { UnitSystem } from "@ctra/utils";

import { useLocalization } from "@base";

import { useChart, useKPISupport } from "../../hooks";
import { useViews } from "../../hooks/useViews";
import { useChartFilters } from "../ChartFilterContext";
import { ChartAPIProviderProps, ContextType } from "./typings";

/**
 * Default Chart API Context
 */
const DefaultContext = createContext<ContextType>({
  chart: "Empty context: no chart" as unknown as ChartEntity,
  dataDescriptor: "Empty context: no data descriptor" as unknown as DataDescriptorEntity,
  api: {
    setChart: _.noop,
    setRequestURL: _.noop
  },
  meta: {
    requestURL: "",
    unitSystem: UnitSystemConversionMap[UnitSystem.metric],
    supportedViews: {} as ContextType["meta"]["supportedViews"],
    farms: { hasNoSupport: [], hasSupport: [] },
    sourceType: ChartDataSourceType.dataPoints
  }
});

/**
 * Provide an API for displaying/updating charts and set meta data used to initialize filter context values
 * @param {string} dataDescriptorID
 * @param {number | undefined} farmID
 * @param {string | undefined} defaultView
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @returns {JSX.Element}
 */
const _ChartAPIProvider: FC<ChartAPIProviderProps> = ({
  dataDescriptorID,
  farmID,
  defaultView,
  children
}) => {
  const { unitSystem: contextUnitSystem } = useLocalization();

  const {
    api: { setMetadata }
  } = useChartFilters();

  /**
   * Resolve the chart object and a basic chart switching API using the data descriptor
   */
  const supportedViews = useViews(dataDescriptorID);
  const [chart, setChart] = useChart(dataDescriptorID, defaultView && supportedViews[defaultView]);

  /**
   * Manage the chart request URL which is necessary for sharing
   */
  const [requestURL, setRequestURL] = useState<string>("");

  /**
   * Farm support information, partitions the farm id list based on chart support
   * @note this is important so when unsupported farms try to access the chart, we can disaply an error message
   */
  const { hasSupport, hasNoSupport } = useKPISupport(dataDescriptorID, farmID ? [farmID] : undefined);

  /**
   * Get the data descriptor and tell whether and imperial data conversion is supported
   * @type {DataDescriptorEntity}
   */
  const dataDescriptorEntity = useSelector<EnterpriseAppState, DataDescriptorEntity>((state) =>
    Enterprise.entities.getDataDescriptor(state, { id: dataDescriptorID })
  );

  /**
   * Always use the context unit system as the default unit system, even is imperial is not supported
   * @type {UnitSystemParam}
   */
  const unitSystem = UnitSystemConversionMap[contextUnitSystem];

  /**
   * Manage the metadata and update the filtering context when the chart ID changes
   * @note this mostly happens when the user switches between charts, and we replace it without remounting
   */
  useEffect(() => {
    const isBackendAggregated = _.some(chart.filters, "isBackendAggregated");

    setMetadata((metadata) => ({
      ...metadata,
      flags: { ...metadata.flags, isBackendAggregated },
      supportedfarmIDs: hasSupport
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart.id]);

  return (
    <DefaultContext.Provider
      value={{
        dataDescriptor: dataDescriptorEntity,
        chart,
        api: {
          setRequestURL,
          setChart
        },
        meta: {
          requestURL,
          supportedViews,
          unitSystem,
          farms: {
            hasSupport,
            hasNoSupport
          },
          sourceType: ChartDataSourceType.dataPoints
        }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const ChartAPIContext = {
  Provider: _ChartAPIProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Hook for Chart context
 * @returns
 */
export const useChartAPI = (): ContextType => useContext<ContextType>(DefaultContext);
